// 📁 src/context/AuthContext.js
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import axios from "../utils/axiosInstance"; // ✅ Auto-attaches JWT token

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
  });

  const [token, setToken] = useState(() => localStorage.getItem("token") || "");
  const [loading, setLoading] = useState(true); // ⏳ Gate downstream contexts

  /* ------------------------------------------------------------------
     ✅ Normalize user object so `_id` always exists
  ------------------------------------------------------------------ */
  const normalizeUser = (data) => {
    if (!data) return null;
    const id = data._id || data.id || data.userId || data.uid || null;

    return {
      ...data,
      _id: id,
      trainingAccess: data.trainingAccess || [], // 🆕 ALWAYS NORMALIZED
    };
  };

  /* ------------------------------------------------------------------
     ⭐ Subscription Fetching (unchanged)
  ------------------------------------------------------------------ */
  const refreshSubscription = useCallback(async () => {
    try {
      if (!token) {
        console.log("⚠️ No token — skipping subscription loading.");
        return null;
      }

      console.log("🔄 Fetching subscription via /api/billing/subscriptions ...");

      const response = await axios.get("/api/billing/subscriptions");
      const subs = Array.isArray(response.data) ? response.data : [];

      const active =
        subs.find(
          (s) =>
            s.revenueStream === "user_subscription" &&
            s.status === "active"
        ) ||
        subs.find((s) => s.status === "active") ||
        null;

      let subscription;

      if (active) {
        subscription = {
          active: true,
          revenueStream: active.revenueStream,
          planCode: active.planCode,
          startDate: active.startDate,
          endDate: active.endDate,
          nextRenewalDate: active.nextRenewalDate,
        };
      } else {
        subscription = { active: false };
      }

      console.log("📦 Subscription resolved:", subscription);

      setUser((u) => {
        if (!u) return u;
        const updated = { ...u, subscription };
        localStorage.setItem("user", JSON.stringify(updated));
        return updated;
      });

      return subscription;
    } catch (err) {
      console.warn("⚠️ Could not fetch subscription:", err.message);

      setUser((u) => {
        if (!u) return u;
        const updated = { ...u, subscription: { active: false } };
        localStorage.setItem("user", JSON.stringify(updated));
        return updated;
      });

      return null;
    }
  }, [token]);

  /* ------------------------------------------------------------------
     🔄 Persistent Login — /api/auth/me
  ------------------------------------------------------------------ */
  const loadUser = useCallback(async () => {
  const localToken = localStorage.getItem("token");



  // 🔥 FORCE LOGIN ONLY ON FIRST APP LOAD (NOT AFTER LOGIN)
const sessionInitialized = sessionStorage.getItem("session_initialized");

if (!sessionInitialized && !localToken) {
  console.log("🔒 First app load — forcing login");

  localStorage.removeItem("token");
  localStorage.removeItem("user");

  sessionStorage.setItem("session_initialized", "true");

  setUser(null);
  setToken("");
  setLoading(false);
  return;
}

  // ✅ Detect Lite mode
  const isLiteMode = window.location.pathname.startsWith("/lite");

  // ✅ Check if Lite user is actually registered
  const isLiteRegistered =
    localStorage.getItem("lite_registration_confirmed") === "true";

  // ✅ No token → guest
  if (!localToken) {
    console.log("⚠️ No token found — user not logged in.");
    setUser(null);
    setLoading(false);
    return;
  }

  // 🔥 CRITICAL FIX — Lite guest mode
  if (isLiteMode && !isLiteRegistered) {
    console.log("🌱 Lite guest mode — skipping user restore");
    setUser(null);
    setLoading(false);
    return;
  }

  try {
    console.log("🔐 Verifying user session via /api/auth/me...");
      const res = await axios.get("/api/auth/me", {
        headers: { Authorization: `Bearer ${localToken}` },
      });

      const normalized = normalizeUser(res.data);
      console.log("✅ User restored from backend:", normalized);

      const userObj = {
        ...normalized,
        subscription: { active: false },
      };

      setUser(userObj);
      setToken(localToken);

      localStorage.setItem("user", JSON.stringify(userObj));
            localStorage.setItem(
        "user_role",
        (userObj.role || "guest").toLowerCase()
      );

      await refreshSubscription();
    } catch (err) {
      console.warn(
        "⚠️ Token invalid or expired:",
        err.response?.data || err.message
      );
      logout();
    } finally {
      setTimeout(() => setLoading(false), 300);
    }
  }, [refreshSubscription]);


  useEffect(() => {
    loadUser();
  }, [loadUser]);

  /* ------------------------------------------------------------------
     🔑 Manual Login
  ------------------------------------------------------------------ */
  const login = async (newToken, userData) => {
  // 🆕 --------------------------------------------------
  // 🧹 CLEAR STALE UPGRADE INTENT ON NORMAL LOGIN
  // ----------------------------------------------------
  const params = new URLSearchParams(window.location.search);
  const intent = params.get("intent");

  // Only KEEP post_auth_intent if this login
  // was explicitly triggered from UpgradePopup
  if (intent !== "upgrade") {
    localStorage.removeItem("post_auth_intent");
  }
  // 🆕 --------------------------------------------------

  const normalized = normalizeUser(userData);
  console.log("✅ User logged in:", normalized);

  const userObj = {
    ...normalized,
    subscription: { active: false },
  };

  setUser(userObj);
  setToken(newToken);

  localStorage.setItem("token", newToken);
  localStorage.setItem("user", JSON.stringify(userObj));
  localStorage.setItem(
  "user_role",
  (userObj.role || "guest").toLowerCase()
);

  await refreshSubscription();

  setTimeout(() => {
    console.log("🕐 AuthContext sync complete after login");
  }, 300);
};

  /* ------------------------------------------------------------------
     🚪 Logout
  ------------------------------------------------------------------ */
  const logout = useCallback(() => {
    console.log("👋 Logging out...");
    setUser(null);
    setToken("");
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.setItem("user_role", "guest");
    
  }, []);

  /* ------------------------------------------------------------------
     ⏱️ Auto-Logout on JWT expiry
  ------------------------------------------------------------------ */
  useEffect(() => {
    if (!token) return;

    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const expiry = payload.exp * 1000;
      const now = Date.now();
      const remaining = expiry - now;

      if (remaining > 0) {
        const timer = setTimeout(() => {
          alert("⚠️ Session expired. Please log in again.");
          logout();
        }, remaining);
        return () => clearTimeout(timer);
      } else {
        logout();
      }
    } catch {
      console.warn("⚠️ Could not decode JWT token.");
    }
  }, [token, logout]);

  /* ------------------------------------------------------------------
     👥 Role Helpers
  ------------------------------------------------------------------ */
  const userRole = user?.role || "guest";
  const isAdmin = userRole === "admin";
  const isInvestor = userRole === "investor";
  const isFounder = userRole === "founder" || userRole === "user";
  const isMentor = userRole === "mentor";

  /* ------------------------------------------------------------------
     🆕 TRAINING HELPERS (M5 READY)
  ------------------------------------------------------------------ */
  const hasTrainingAccess = (trainingCode) =>
    user?.trainingAccess?.includes(trainingCode);

  /* ------------------------------------------------------------------
     🧠 Debug
  ------------------------------------------------------------------ */
  useEffect(() => {
    console.log("🧭 AuthContext update:", { user, token });
  }, [user, token]);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        login,
        logout,
        userRole,
        isAdmin,
        isInvestor,
        isFounder,
        isMentor,
        loading,

        // ⭐ Existing
        refreshSubscription,

        // 🆕 M5 EXPORTS
        hasTrainingAccess,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
